/**
 * storage.ts - the ONLY module allowed to touch browser storage for auth
 * tokens. Centralizing this makes Section 56's rules ("avoid storing
 * passwords/OTPs/card details") easy to audit - there's exactly one place
 * that writes to storage at all.
 *
 * We keep the access token in memory (a module-level variable) rather than
 * localStorage - it disappears on a hard refresh, which is fine because
 * the refresh token (in an HTTP-only cookie set by the backend, per the
 * spec's Section 5.6 recommendation) silently gets a new one on load. This
 * means an XSS bug can't just read a long-lived token out of
 * localStorage - there's nothing sitting there to read.
 *
 * NOTE: this assumes the backend sets the refresh token as an HTTP-only
 * cookie. The TripLink backend built so far returns the refresh token in
 * the JSON response body instead (see AuthResponse) - until that's changed
 * server-side, this module falls back to sessionStorage for the refresh
 * token (cleared when the tab closes, unlike localStorage) as the least-bad
 * option, clearly marked below so it's easy to find and upgrade later.
 */
let inMemoryAccessToken: string | null = null;

const REFRESH_TOKEN_KEY = 'triplink.refreshToken'; // TODO: remove once the backend sets this as an HTTP-only cookie instead (Section 5.6)

export const tokenStorage = {
  getAccessToken(): string | null {
    return inMemoryAccessToken;
  },
  setAccessToken(token: string | null) {
    inMemoryAccessToken = token;
  },
  getRefreshToken(): string | null {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },
  setRefreshToken(token: string | null) {
    if (token) {
      sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  },
  clearAll() {
    inMemoryAccessToken = null;
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};
